import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api, getApiErrorMessage } from '../api/client'

interface User {
  id: string
  email: string
  name: string
  avatar?: string
  role: 'USER' | 'ADMIN'
  isSuperAdmin?: boolean
  isApproved: boolean
}

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  isInitialized: boolean
  error: string | null

  // Actions
  setUser: (user: User | null) => void
  setToken: (token: string | null) => void
  setError: (error: string | null) => void
  setAuthData: (token: string, user: User) => void
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name: string) => Promise<string>
  logout: () => void
  initializeAuth: () => void
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isInitialized: false,
      error: null,

      setUser: (user) => set({ user }),
      setToken: (token) => set({ token }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
      setAuthData: (token, user) => {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`
        set({ token, user })
      },

      login: async (email, password) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/auth/login', { email, password })
          const { token, user } = response.data.data
          set({ token, user, isLoading: false })
        } catch (error: unknown) {
          set({
            error: getApiErrorMessage(error, 'Login failed'),
            isLoading: false,
          })
          throw error
        }
      },

      register: async (email, password, name) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/auth/register', { email, password, name })
          set({ isLoading: false })
          return response.data.message as string
        } catch (error: unknown) {
          set({
            error: getApiErrorMessage(error, 'Registration failed'),
            isLoading: false,
          })
          throw error
        }
      },

      logout: () => {
        set({ user: null, token: null, error: null })
      },

      initializeAuth: () => {
        const { token } = get()
        if (token) {
          // Set auth header
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
          // Fetch current user
          api.get('/auth/me')
            .then((response) => {
              set({ user: response.data.data.user, isInitialized: true })
            })
            .catch(() => {
              // Token invalid, clear it
              set({ token: null, user: null, isInitialized: true })
              delete api.defaults.headers.common['Authorization']
            })
        } else {
          set({ isInitialized: true })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ token: state.token }),
    }
  )
)
